import { useState, useCallback, useEffect, useRef } from "react";
import Cropper from "react-easy-crop";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import PhotoCameraOutlined from "@mui/icons-material/PhotoCameraOutlined";
import RotateLeftOutlined from "@mui/icons-material/RotateLeftOutlined";
import RotateRightOutlined from "@mui/icons-material/RotateRightOutlined";
import ZoomInOutlined from "@mui/icons-material/ZoomInOutlined";
import getCroppedImg from "utils/file/getCroppedImg";
import { getPhotoUrl } from "utils/helpers/files";
import photoService from "services/File/PhotoService";

/**
 * Created by Danilo Lazo
 * Reusable avatar placeholder -> pick file -> crop -> upload flow.
 * Also displays an existing photo (already saved on the server) on mount.
 *
 * Usage (public image, no auth needed to view it):
 *   <PhotoUploadCrop
 *       sourceImage={form?.photoUrl}
 *       onUpload={(blob) => photoService.upload(blob)}
 *       onUploadSuccess={(result) => updateField("photoUrl", result?.url)}
 *   />
 *
 * Usage (image endpoint requires the Authorization header, e.g. served by
 * the same protected API as the upload):
 *   <PhotoUploadCrop
 *       sourceImage={form?.photoUrl}
 *       fetchImage={(src) => photoService.fetch(src)}   // returns a Blob
 *       onUpload={(blob) => photoService.upload(blob)}
 *       onUploadSuccess={(result) => updateField("photoUrl", result?.url)}
 *   />
 *
 * Props:
 *   sourceImage       - existing photo reference to display (URL or path, string | undefined)
 *   fetchImage        - optional async (sourceImage) => Blob | string. Use this when the
 *                        image can't be loaded with a plain <img src> (e.g. it needs an
 *                        Authorization header). If omitted, sourceImage is used directly
 *                        as the <img> src.
 *   onUpload          - async (blob) => result. If omitted, onUploadSuccess
 *                        receives the raw cropped Blob instead (no network call).
 *   onUploadSuccess   - called with whatever onUpload resolves to
 *   size              - avatar diameter in px (default 120)
 *   aspect            - crop aspect ratio (default 1 = square)
 *   cropShape         - "round" | "rect" (default "round")
 *   label             - caption under the avatar
 */
export default function PhotoUploadCrop({
    sourceImage,
    // fetchImage,
    // onUpload,
    onUploadSuccess,
    size = 120,
    aspect = 1,
    cropShape = "round",
    label,
    readOnly = false
}) {
    const [imageSrc, setImageSrc] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);

    const onUpload = photoService.upload;
    const fetchImage = photoService.fetch;

    // photo just cropped in this session (shown instantly, before/without waiting on upload)
    const [localPreview, setLocalPreview] = useState(null);
    const localPreviewRef = useRef(null);

    // existing photo loaded via fetchImage (when the image needs an authenticated request)
    const [remotePreview, setRemotePreview] = useState(null);
    const [loadingExisting, setLoadingExisting] = useState(false);
    const remotePreviewRef = useRef(null);
    const lastLoadedSourceRef = useRef(null);

    // load the existing photo whenever sourceImage changes (e.g. on mount, or when
    // switching to a different record) — only needed when fetchImage is provided
    useEffect(() => {
        if (!sourceImage || !fetchImage) {
            setRemotePreview(null);
            return;
        }

        if (lastLoadedSourceRef.current === sourceImage) {
            return; // already loaded this exact source
        }

        let cancelled = false;

        const paddedUrl = getPhotoUrl(sourceImage);
        (async () => {
            try {
                setLoadingExisting(true);
                const result = await fetchImage(paddedUrl);
                if (cancelled) return;

                const url =
                    result instanceof Blob
                        ? URL.createObjectURL(result)
                        : result;

                if (remotePreviewRef.current)
                    URL.revokeObjectURL(remotePreviewRef.current);
                remotePreviewRef.current = result instanceof Blob ? url : null;

                lastLoadedSourceRef.current = paddedUrl;
                setRemotePreview(url);
            } catch {
                // couldn't load the existing photo — fall back to the placeholder icon
                if (!cancelled) setRemotePreview(null);
            } finally {
                if (!cancelled) setLoadingExisting(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [sourceImage, fetchImage]);

    // revoke any object URLs we created when the component unmounts
    useEffect(() => {
        return () => {
            if (localPreviewRef.current)
                URL.revokeObjectURL(localPreviewRef.current);
            if (remotePreviewRef.current)
                URL.revokeObjectURL(remotePreviewRef.current);
        };
    }, []);

    const onCropComplete = useCallback((_, areaPixels) => {
        setCroppedAreaPixels(areaPixels);
    }, []);

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            setError("Please select an image file.");
            e.target.value = "";
            return;
        }

        setError(null);
        const reader = new FileReader();
        reader.addEventListener("load", () => {
            setImageSrc(reader.result);
            setDialogOpen(true);
        });
        reader.readAsDataURL(file);

        // allow re-selecting the same file later
        e.target.value = "";
    };

    const handleClose = () => {
        if (uploading) return;
        setDialogOpen(false);
        setImageSrc(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
        setCroppedAreaPixels(null);
    };

    const handleSave = async () => {
        if (!croppedAreaPixels) return;

        try {
            setUploading(true);
            setError(null);

            const croppedBlob = await getCroppedImg(
                imageSrc,
                croppedAreaPixels,
                rotation
            );

            // show the cropped photo immediately, don't wait for the upload to finish
            const objectUrl = URL.createObjectURL(croppedBlob);
            if (localPreviewRef.current)
                URL.revokeObjectURL(localPreviewRef.current);
            localPreviewRef.current = objectUrl;
            setLocalPreview(objectUrl);

            if (onUpload) {
                const result = await onUpload(croppedBlob);
                onUploadSuccess?.(result);
            } else {
                onUploadSuccess?.(croppedBlob);
            }

            handleClose();
        } catch (err) {
            // upload failed - drop the optimistic preview, fall back to the last saved photo
            if (localPreviewRef.current) {
                URL.revokeObjectURL(localPreviewRef.current);
                localPreviewRef.current = null;
            }
            setLocalPreview(null);
            setError(
                err?.response?.data?.message ||
                    err?.message ||
                    "Failed to upload photo."
            );
        } finally {
            setUploading(false);
        }
    };

    // priority: freshly cropped photo > existing photo (fetched or plain URL)
    const plainSource = fetchImage ? null : sourceImage;
    const displaySrc =
        localPreview || remotePreview || plainSource || undefined;
    const showPlaceholder = !displaySrc && !loadingExisting;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5
            }}
        >
            <Box sx={{ position: "relative", width: size, height: size }}>
                <Avatar
                    src={displaySrc}
                    sx={{ width: size, height: size, bgcolor: "grey.200" }}
                >
                    {loadingExisting && <CircularProgress size={size * 0.35} />}
                    {showPlaceholder && (
                        <PhotoCameraOutlined
                            sx={{ fontSize: size * 0.4, color: "grey.500" }}
                        />
                    )}
                </Avatar>

                {!readOnly && (
                    <IconButton
                        component="label"
                        size="small"
                        sx={{
                            position: "absolute",
                            bottom: 0,
                            right: 0,
                            bgcolor: "primary.main",
                            color: "common.white",
                            "&:hover": { bgcolor: "primary.dark" }
                        }}
                    >
                        <PhotoCameraOutlined fontSize="small" />
                        <input
                            hidden
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                        />
                    </IconButton>
                )}
            </Box>

            {label && (
                <Typography variant="caption" color="text.secondary">
                    {label}
                </Typography>
            )}

            {error && (
                <Typography variant="caption" color="error">
                    {error}
                </Typography>
            )}

            <Dialog
                open={dialogOpen}
                onClose={handleClose}
                maxWidth="xs"
                fullWidth
            >
                <DialogContent sx={{ p: 0 }}>
                    <Box
                        sx={{
                            position: "relative",
                            width: "100%",
                            height: 320,
                            bgcolor: "grey.900"
                        }}
                    >
                        {imageSrc && (
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                rotation={rotation}
                                aspect={aspect}
                                cropShape={cropShape}
                                showGrid={false}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onRotationChange={setRotation}
                                onCropComplete={onCropComplete}
                            />
                        )}
                    </Box>
                    <Box sx={{ px: 3, py: 2 }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                        >
                            <ZoomInOutlined
                                fontSize="small"
                                sx={{ color: "grey.600" }}
                            />
                            <Slider
                                value={zoom}
                                min={1}
                                max={3}
                                step={0.1}
                                onChange={(_, v) => setZoom(v)}
                                aria-label="Zoom"
                            />
                        </Stack>

                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{ mt: 1 }}
                        >
                            <IconButton
                                size="small"
                                onClick={() =>
                                    setRotation((r) => (r - 90 + 360) % 360)
                                }
                            >
                                <RotateLeftOutlined fontSize="small" />
                            </IconButton>

                            <Slider
                                value={rotation}
                                min={0}
                                max={360}
                                step={1}
                                onChange={(_, v) => setRotation(v)}
                                aria-label="Rotation"
                            />

                            <IconButton
                                size="small"
                                onClick={() =>
                                    setRotation((r) => (r + 90) % 360)
                                }
                            >
                                <RotateRightOutlined fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleClose} disabled={uploading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={uploading}
                    >
                        {uploading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            "Save Photo"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
